/* paex-shipping.js — Shipping & delivery page (sections/paex-shipping-*.liquid).
   NOT a new engine: the postal-code checker is another surface of the delivery-
   method logic — destination from assets/paex-store-core.js (paexDestRead /
   paexDestWrite, sessionStorage "paex:dest", event "paex:dest-change"; geo chain
   via "paex:geo-ready"), dates from assets/paex-edd.js (paexEddEstimate,
   paexEddOrderBy, paexEddLiveFetch via the AfterShip app proxy, formatters).
   Zone / method / price come from the theme settings paex_shipping_zone_rules
   and paex_shipping_rate_table (parsed here). Also drives the mobile sticky
   mini-checker, the Mapbox route map, the rates table, the store-pickup list,
   the help chat opener, the reviews arrows and the performance line.
   Loaded defer after paex-store-core.js and paex-edd.js. */
(function () {
  'use strict';

  var EV_DEST = 'paex:dest-change';
  var EV_GEO = 'paex:geo-ready';
  var EV_RESOLVE = 'paex:shipping-resolve';
  var MAPBOX_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.js';
  var MAPBOX_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.css';
  var PROVINCE_EN = { AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick', NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon' };
  var METHOD_BY_ZONE = { metro: 'standard', regional: 'regional', remote: 'remote' };
  // Free shipping offer - PE (2026-10-19): rate title → method, and the short
  // offer cell per zone from the engine's config (metaobject values, cents)
  function paexOfferMethodOf(name) {
    var text = String(name || '');
    if (/remote/i.test(text)) return 'remote';
    if (/regional/i.test(text)) return 'regional';
    if (/standard/i.test(text)) return 'standard';
    return null;
  }
  function paexOfferCellText(engine, zoneKey) {
    var offer = engine.config.offers[zoneKey];
    var texts = engine.config.texts || {};
    if (!offer || offer.type === 'none') return ''; // remote: no offer line (store pickup not offered yet — Sasha 2026-10-20)
    var pct = offer.type === 'percent_off';
    var values = { threshold: engine.moneyShort(Number(offer.threshold) || 0), value: pct ? String(Number(offer.value) || 0) + (/^fr/i.test((window.Shopify && Shopify.locale) || '') ? '\u00a0%' : '%') : engine.moneyShort(Number(offer.value) || 0) };
    if (offer.type === 'free') return engine.fill(texts.cellFree || '', values);
    if (offer.type === 'amount_off' || pct) return engine.fill(texts.cellOff || '', values);
    return '';
  }
  var fsaCache = {}; // url -> Promise<table>
  var lastResolve = null; // last checker verdict, replayed to late-connecting sections

  function fmt(template, values) {
    if (typeof paexDeliveryFormat === 'function') return paexDeliveryFormat(template, values || {});
    return String(template == null ? '' : template);
  }
  function readJson(host, selector) {
    var node = host.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (error) { return null; }
  }
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }
  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }
  function locale() {
    var raw = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
    return raw.indexOf('-') > -1 ? raw : raw + '-CA';
  }
  function money(value) {
    var number = Number(value);
    if (!isFinite(number)) return null;
    // Visual review 2026-10-23 (#37): the site's price format ("11.99 $" — shop.money_format)
    // through the shipping-offer engine when it is on the page, i.e. the SAME formatter as the
    // "Free over 99 $*" line under it; Intl (symbol first) only without the engine.
    var engine = window.paexShippingOffer;
    if (engine && typeof engine.money === 'function') {
      try { var text = engine.money(Math.round(number * 100)); if (text) return text; } catch (error) { /* ladder below */ }
    }
    // the engine's own config island carries shop.money_format even before the engine script
    // has run (both are deferred): apply the "{{amount}} $" template rather than Intl's "$9.99"
    try {
      var island = document.querySelector('[data-paex-fso-config]');
      var template = island ? String((JSON.parse(island.textContent) || {}).moneyFormat || '') : '';
      if (template.indexOf('{{') !== -1) {
        var fixed = number.toFixed(2);
        return template
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(number)))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, fixed.replace('.', ','))
          .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, fixed.replace('.', ','))
          .replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, fixed)
          .replace(/<[^>]*>/g, '');
      }
    } catch (error) { /* Intl below */ }
    try {
      return new Intl.NumberFormat(locale(), {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD',
        minimumFractionDigits: 2,
      }).format(number);
    } catch (error) { return number.toFixed(2); }
  }
  function cutoffLabel(cutoff, texts) {
    var parts = String(cutoff || '12:00').split(':');
    // #37: the shared 12-hour formatter (paexFulfil.fmtTime — "noon" / "2:30 p.m.", FR « midi » /
    // « 14 h 30 »), the same clock as every BOPIS surface; the legacy Intl label stays the fallback.
    var api = window.paexFulfil;
    var hour = Number(parts[0]);
    if (api && typeof api.fmtTime === 'function' && isFinite(hour)) {
      try {
        var shared = api.fmtTime(hour, Number(parts[1]) || 0);
        if (shared && !/^Translation missing/i.test(String(shared))) return String(shared);
      } catch (error) { /* legacy below */ }
    }
    if (texts && texts.noon && Number(parts[0]) === 12 && (Number(parts[1]) || 0) === 0) return texts.noon;
    var date = new Date(Date.UTC(2026, 0, 1, Number(parts[0]) || 12, Number(parts[1]) || 0));
    try {
      var label = new Intl.DateTimeFormat(locale(), { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(date);
      return texts && texts.cutoff_tz ? label + ' ' + texts.cutoff_tz : label;
    } catch (error) { return String(cutoff); }
  }
  // #37: the occasion cutoff line is LABELLED so it never reads as a second arrival date —
  // paex.fulfil.cutoff_order_by through paexFulfil.t ("Halloween cutoff: order by Mon, Oct 26,
  // noon to get it by Thu, Oct 29"); the locale order_by sentence stays the fallback.
  function cutoffOrderBy(texts, values) {
    var api = window.paexFulfil;
    if (api && typeof api.t === 'function' && values.occasion) {
      try {
        var text = api.t('cutoff_order_by', values);
        if (text && !/^Translation missing/i.test(String(text)) && String(text).indexOf('{') === -1) return String(text);
      } catch (error) { /* fallback below */ }
    }
    return fmt(texts.order_by, values);
  }
  function dateKey(date) {
    return paexEddKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }
  function todayKey(tz) {
    var now = paexEddWarehouseNow(tz);
    return paexEddKey(now.y, now.m, now.d);
  }
  function dateFromKey(key) {
    var parts = String(key).split('-');
    return new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12));
  }
  function cleanPostal(value) { return String(value || '').replace(/\s+/g, '').toUpperCase(); }
  function isFullPostal(value) { return /^[ABCEGHJ-NPRSTVXY]\d[A-Z]\d[A-Z]\d$/.test(cleanPostal(value)); }
  function isPostalGrade(value) { return /^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(cleanPostal(value)); }
  function prettyPostal(value) { var raw = cleanPostal(value); return raw.length === 6 ? raw.slice(0, 3) + ' ' + raw.slice(3) : raw; }
  function reducedMotion() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function cssVar(node, name) { return getComputedStyle(node).getPropertyValue(name).trim(); }
  function rgbVar(node, name, alpha) {
    var triplet = cssVar(node, name);
    if (!triplet) return null;
    return alpha == null ? 'rgb(' + triplet + ')' : 'rgba(' + triplet + ',' + alpha + ')';
  }

  /* ---------- zone + rate tables (theme settings) ---------- */
  function parseZones(text) {
    var rules = [];
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length !== 2) return;
      var prefix = parts[0].trim().toUpperCase();
      var zone = parts[1].trim().toLowerCase();
      if (!prefix || !/^(metro|regional|remote)$/.test(zone)) return;
      rules.push([prefix, zone]);
    });
    return rules;
  }
  // Longest matching prefix wins; a 2+ character match overrides the built-in
  // rural (second character 0) / territory (X, Y) = remote rule; * is the default.
  // Since 2026-10-19 (Free shipping offer - PE) the shared paexZoneFor in
  // paex-store-core.js is the one matcher — pure prefix rules seeded from the
  // Checkout Blocks lists, no rural/territory shortcut (unlisted rural FSAs ARE
  // Canada Standard at checkout). The legacy body below only runs when
  // store-core is missing.
  function zoneFor(postal, rules) {
    if (typeof paexZoneFor === 'function') return paexZoneFor(postal, rules) || (/^[A-Z]\d/.test(cleanPostal(postal)) ? 'metro' : null);
    var raw = cleanPostal(postal);
    if (!/^[A-Z]\d/.test(raw)) return null;
    var best = null;
    var fallback = null;
    rules.forEach(function (rule) {
      if (rule[0] === '*') { fallback = rule[1]; return; }
      if (raw.indexOf(rule[0]) === 0 && (!best || rule[0].length > best[0].length)) best = rule;
    });
    if (best && best[0].length >= 2) return best[1];
    if (raw.charAt(1) === '0' || /^[XY]/.test(raw)) return 'remote';
    if (best) return best[1];
    return fallback || 'regional';
  }
  function parsePrice(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw || /^(—|–|-|n\/a|na)$/i.test(raw)) return null;
    var number = Number(raw.replace(/[^0-9.,]/g, '').replace(',', '.'));
    return isFinite(number) ? number : null;
  }
  function parseRates(text) {
    var table = {};
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 3) return;
      var province = parts[0].trim().toUpperCase();
      var method = parts[1].trim().toLowerCase();
      if (!province || !/^(standard|regional|remote|express)$/.test(method)) return;
      // a cell may be a range "44.95-54.95" (cart-value bands above the split, lowest first)
      var overParts = String(parts[2] || '').split(/\s*[-–]\s*(?=\d)/);
      table[province] = table[province] || {};
      table[province][method] = { over: parsePrice(overParts[0]), overMax: overParts.length > 1 ? parsePrice(overParts[overParts.length - 1]) : null, under: parsePrice(parts[3]) };
    });
    return table;
  }
  function ratesFor(province, table) {
    return table[String(province || '').toUpperCase()] || table['*'] || null;
  }
  // Rates are handled as TIERS by cart value: { min, max|null, price }.
  // Source 1 (preferred): the shop's own shipping settings via the EDD proxy
  // /rates (Admin API deliveryProfiles, cached 1 h at the edge + per session).
  // Source 2 (fallback): the theme textarea (over $25 / $25 and under).
  var adminRatesCache = {}; // url -> Promise<payload|null>
  var TIER_SPLIT = 25;
  function adminRates(url) {
    if (!url) return Promise.resolve(null);
    if (!adminRatesCache[url]) {
      var storageKey = 'paex:shiprates:' + url;
      var hit = null;
      try { var raw = sessionStorage.getItem(storageKey); if (raw) { var parsed = JSON.parse(raw); if (parsed && parsed.t && Date.now() - parsed.t < 3600000) hit = parsed.d; } } catch (error) { /* no storage */ }
      adminRatesCache[url] = hit ? Promise.resolve(hit) : fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          if (!data || !data.zones) return null;
          try { sessionStorage.setItem(storageKey, JSON.stringify({ t: Date.now(), d: data })); } catch (error) { /* no storage */ }
          return data;
        })
        .catch(function () { return null; });
    }
    return adminRatesCache[url];
  }
  function tableTiers(rate) {
    if (!rate || rate.over == null) return null;
    var under = rate.under == null ? rate.over : rate.under;
    var tiers = [{ min: 0, max: TIER_SPLIT, price: under }];
    if (rate.overMax != null && rate.overMax !== rate.over) {
      // a banded method (e.g. remote): only the band edges are known from the table
      tiers.push({ min: TIER_SPLIT + 0.01, max: 100, price: rate.over });
      tiers.push({ min: 100.01, max: null, price: rate.overMax });
    } else {
      tiers.push({ min: TIER_SPLIT + 0.01, max: null, price: rate.over });
    }
    return tiers;
  }
  // { standard: {name, tiers}, regional: …, … } for a province — admin zone first, table second
  function methodsFor(province, admin, table) {
    var code = String(province || '').toUpperCase();
    if (admin && admin.zones) {
      var zone = null;
      admin.zones.forEach(function (z) { if (!zone && (z.provinces || []).indexOf(code) > -1) zone = z; });
      if (zone) {
        var out = {};
        (zone.methods || []).forEach(function (m) {
          if (!m.tiers || !m.tiers.length || m.key === 'pickup' || m.key === 'other') return;
          if (!out[m.key]) out[m.key] = { name: m.name, tiers: m.tiers, admin: true };
        });
        return { methods: out, admin: true, zoneName: zone.name };
      }
    }
    var rates = ratesFor(code, table);
    if (!rates) return null;
    var fallback = {};
    Object.keys(rates).forEach(function (key) { var tiers = tableTiers(rates[key]); if (tiers) fallback[key] = { name: '', tiers: tiers, admin: false }; });
    return Object.keys(fallback).length ? { methods: fallback, admin: false } : null;
  }
  function tierFor(tiers, total) {
    var match = null;
    (tiers || []).forEach(function (t) { if (total >= t.min - 0.005 && (t.max == null || total <= t.max + 0.005)) match = match || t; });
    return match;
  }
  // the price band for carts over the split ("lowest tier first") and the one at / under it
  function overSplit(tiers) {
    var list = (tiers || []).filter(function (t) { return t.max == null || t.max > TIER_SPLIT; });
    if (!list.length) return null;
    var prices = list.map(function (t) { return t.price; });
    return { min: Math.min.apply(null, prices), max: Math.max.apply(null, prices) };
  }
  function underSplit(tiers) {
    var t = tierFor(tiers, TIER_SPLIT) || (tiers && tiers[0]);
    return t ? t.price : null;
  }
  function methodsRange(methods) {
    var values = [];
    Object.keys(methods || {}).forEach(function (key) { (methods[key].tiers || []).forEach(function (t) { values.push(t.price); }); });
    if (!values.length) return null;
    return { min: Math.min.apply(null, values), max: Math.max.apply(null, values) };
  }
  function moneyRange(range) {
    if (!range) return null;
    return range.min === range.max ? money(range.min) : money(range.min) + '–' + money(range.max);
  }

  /* ---------- FSA centroids (assets/paex-fsa-centroids.json) ---------- */
  function fsaTable(url) {
    if (!url) return Promise.resolve({});
    if (!fsaCache[url]) {
      fsaCache[url] = fetch(url).then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) { return (data && data.fsa) || {}; })
        .catch(function () { return {}; });
    }
    return fsaCache[url];
  }
  function centroidFor(postal, table) {
    var keys = typeof paexPostalKeys === 'function' ? paexPostalKeys(postal) : null;
    if (!keys) return null;
    for (var i = 0; i < keys.length; i += 1) {
      var hit = table[keys[i]];
      if (hit && isFinite(hit[0]) && isFinite(hit[1])) return { lat: Number(hit[0]), lng: Number(hit[1]) };
    }
    return null;
  }
  function distanceKm(a, b) {
    return typeof paexDeliveryDistanceKm === 'function' ? paexDeliveryDistanceKm(a.lat, a.lng, b.lat, b.lng) : NaN;
  }
  function nearestStores(origin, stores, limit) {
    var list = [];
    Object.keys(stores || {}).forEach(function (id) {
      var store = stores[id];
      if (!store || !isFinite(Number(store.lat)) || !isFinite(Number(store.lng))) return;
      if (typeof paexStoreExpired === 'function' && paexStoreExpired(store)) return;
      var km = distanceKm(origin, { lat: Number(store.lat), lng: Number(store.lng) });
      if (!isFinite(km)) return;
      list.push({ id: id, store: store, km: km });
    });
    list.sort(function (a, b) { return a.km - b.km; });
    return limit ? list.slice(0, limit) : list;
  }
  function kmLabel(texts, km) {
    var value = km < 10 ? Math.round(km * 10) / 10 : Math.round(km);
    var text;
    try { text = new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 }).format(value); } catch (error) { text = String(value); }
    return fmt(texts.km, { km: text });
  }
  // BOPIS plan 6b-3: EN hour labels switch to 12-hour ("9 p.m.") through the
  // shared formatter window.paexFulfil.fmtTime (paex-fulfil.js) — only when the
  // template carries the legacy "{close}:00" suffix (EN locale strings); FR
  // ("{close} h") and a page without paexFulfil render exactly as today.
  function hourLabel(template, key, value) {
    var api = window.paexFulfil;
    var tpl = String(template == null ? '' : template);
    var token = '{' + key + '}:00';
    var values = {};
    if (api && typeof api.fmtTime === 'function' && value != null && tpl.indexOf(token) !== -1) {
      var parts = String(value).split(':');
      var hour = parseInt(parts[0], 10);
      var minute = parseInt(parts[1] || '0', 10) || 0;
      var label = '';
      if (isFinite(hour)) { try { label = api.fmtTime(hour, minute); } catch (error) { label = ''; } }
      if (label) { values[key] = label; return fmt(tpl.split(token).join('{' + key + '}'), values); }
    }
    values[key] = value;
    return fmt(tpl, values);
  }
  function hoursText(store, texts, tz) {
    if (typeof paexStoreHoursText !== 'function') return '';
    var keys = {
      openNow: texts.open_now, closesAt: texts.closes_at, closesSoon: texts.closes_soon,
      opensAt: texts.opens_at, opensTomorrow: texts.opens_tomorrow,
    };
    var api = window.paexFulfil;
    if (!(api && typeof api.fmtTime === 'function') || typeof paexStoreHoursInfo !== 'function') {
      return paexStoreHoursText(store, keys, { timeZone: tz });
    }
    // same branches as paexStoreHoursText (core), with the 12-hour hour label
    var info = paexStoreHoursInfo(store, null, { timeZone: tz });
    if (!info) return '';
    if (info.state === 'open' && keys.openNow && keys.closesAt) return keys.openNow + ' · ' + hourLabel(keys.closesAt, 'close', info.closeHour);
    if (info.state === 'closes_soon' && keys.closesSoon) return hourLabel(keys.closesSoon, 'close', info.closeHour);
    if (info.nextOpenHour != null) {
      if (info.nextOpenIsTomorrow && keys.opensTomorrow) return hourLabel(keys.opensTomorrow, 'open', info.nextOpenHour);
      if (keys.opensAt) return hourLabel(keys.opensAt, 'open', info.nextOpenHour);
    }
    return '';
  }
  /* BOPIS (2026-10-22, plan Track 3.5/3.6): participating-store lines on the
     pickup cards. Strings = paex.fulfil.* (texts island); flags / terms = the
     store blob (seams §3.4); pausedToday / readyLine / fmtTime from
     window.paexFulfil when present, local fallbacks otherwise. */
  function safeText(text) { return text && !/^Translation missing/i.test(text) ? text : ''; }
  function fulfilPausedToday(store, service, tz) {
    if (!store) return true;
    var api = window.paexFulfil;
    if (api && typeof api.pausedToday === 'function') { try { return Boolean(api.pausedToday(store, service)); } catch (error) { /* local rule below */ } }
    if (service === 'pickup' ? store.pickupPaused === true : store.localPaused === true) return true;
    var dates = Array.isArray(store.blackoutDates) ? store.blackoutDates : [];
    if (!dates.length || typeof paexStoreNowParts !== 'function') return false;
    var today = paexStoreNowParts(new Date(), tz || 'America/Toronto').dateKey;
    return Boolean(today) && dates.indexOf(today) !== -1;
  }
  // metaobject money / number / text → display amount (money type emits {amount, currency_code})
  function fulfilAmount(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'object') {
      var amount = money(value.amount != null ? value.amount : value.value);
      return amount || '';
    }
    // a number is CENTS: the store blob is printed by Liquid, where a money metafield's
    // `.value | json` is the Money drop serialised as an integer of cents (9.99 $ → 999)
    var number = Number(value);
    if (isFinite(number)) return money(number / 100) || String(value);
    return String(value);
  }
  function fulfilCutoff(cutoff) {
    var raw = String(cutoff == null ? '' : cutoff).trim();
    if (!raw) return '';
    var api = window.paexFulfil;
    var m = /^(\d{1,2})(?::(\d{2}))?/.exec(raw);
    if (api && typeof api.fmtTime === 'function' && m) {
      try { var label = api.fmtTime(parseInt(m[1], 10), parseInt(m[2] || '0', 10) || 0); if (label) return label; } catch (error) { /* raw below */ }
    }
    return raw;
  }
  // Lane D3 contract (2026-10-23): readyLine().text is the ONE full sentence ("Usually ready in
  // 2 hours · held 3 days" / closed "Ready tomorrow around noon (2 hours after we open at
  // 10 a.m.) · held 3 days") — consumed as is, never concatenated with the local base; the
  // texts-island base (prep + hold) only answers when the API is absent.
  function fulfilReadyLine(store, texts) {
    var api = window.paexFulfil;
    if (api && typeof api.readyLine === 'function') {
      try { var ready = api.readyLine(store, new Date()); if (ready && ready.text) return String(ready.text); } catch (error) { /* local below */ }
    }
    var prep = Number(store.prepHours);
    var hold = Number(store.holdDays);
    var prepText = prep === 1 ? safeText(texts.prep_hours_one) : fmt(safeText(texts.prep_hours), { n: isFinite(prep) ? prep : '' });
    var holdText = hold === 1 ? safeText(texts.hold_days_one) : fmt(safeText(texts.hold_days), { n: isFinite(hold) ? hold : '' });
    var base = safeText(texts.ready_base) ? fmt(texts.ready_base, { prep: prepText, hold: holdText }) : '';
    // seams §3.5: ready_base says "held {hold}" while hold_days is "held {n} days"
    // (FR « conservé … ») — collapse the doubled word whichever way Lane B settles it
    return base.replace(/(^|\s)(\S+) \2(?=\s|$)/g, '$1$2');
  }
  // Design 8.1 (export 4): the pickup line on the store card = "Pick up in store, free · " +
  // readyLine().text (page_pickup_free from the strings snippet; the ready sentence alone when
  // the key is blank). The same-day line keeps its fee · minimum · when (the design keeps the
  // fee on the shipping page).
  function fulfilPickupLine(store, texts) {
    return [safeText(texts.page_pickup_free), fulfilReadyLine(store, texts)].filter(Boolean).join(' · ');
  }
  // A store is in the program today when pickup or same-day is enabled and not paused /
  // blacked out (the chip's rule, paex-store-chip.js fulfilPills).
  function fulfilParticipates(store, tz) {
    if (!store) return false;
    return (store.pickup === true && !fulfilPausedToday(store, 'pickup', tz)) || (store.local === true && !fulfilPausedToday(store, 'local', tz));
  }
  // The chosen store, resolved like the rest of the theme (visual review #35): paexFulfil.storeId()
  // = chip cookie paex_preferred_store → RCC cookie → account preferred store → postal / geo
  // origin; without the API the chip cookie alone.
  function fulfilChosenStoreId(stores) {
    var api = window.paexFulfil;
    var id = null;
    if (api && typeof api.storeId === 'function') { try { id = api.storeId(); } catch (error) { id = null; } }
    if (!id) {
      var name = typeof PAEX_STORE_COOKIE === 'string' ? PAEX_STORE_COOKIE : 'paex_preferred_store';
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      id = m ? decodeURIComponent(m[1]) : null;
    }
    id = id == null ? '' : String(id);
    return id && stores && stores[id] ? id : null;
  }
  // Round 2 (n=49): the same-day status is the computed, time-aware line of plan 6c
  // (paexFulfil.localLine: an open day today and before the cutoff → "Today if you order
  // before {cutoff}", otherwise "Delivered {tomorrow | next open day}" — holidays and
  // blackout dates skipped), never a static "today". localLine needs a postal code to
  // answer, and the shipping card has none, so it is probed with a postal that is in the
  // store's own zone by construction (the first local_delivery_postal_prefixes entry that
  // pads to a postal shape — the list is walked past malformed entries; "*" accepts every
  // postal, so any valid probe will do); when the API is absent, no entry yields a valid
  // probe or the line is still refused, the card prints the plain terms only (design 8.1).
  var FULFIL_POSTAL_SHAPE = /^[A-Z]\d[A-Z](\d[A-Z]\d)?$/; // = paexFulfil's postalish()
  function fulfilProbePostal(store) {
    var raw = store && store.localPrefixes;
    var list = Array.isArray(raw) ? raw : String(raw == null ? '' : raw).split(/[\s,;|]+/);
    for (var i = 0; i < list.length; i += 1) {
      var prefix = String(list[i] == null ? '' : list[i]).replace(/\s+/g, '').toUpperCase();
      if (!prefix) continue;
      if (prefix === '*') return 'A0A';
      var probe = prefix;
      while (probe.length < 3) probe += probe.length % 2 === 0 ? 'A' : '0';
      if (probe.length > 3) while (probe.length < 6) probe += probe.length % 2 === 0 ? 'A' : '0';
      if (FULFIL_POSTAL_SHAPE.test(probe)) return probe;
    }
    return '';
  }
  function fulfilLocalWhen(store) {
    var api = window.paexFulfil;
    if (!api || typeof api.localLine !== 'function') return '';
    var probe = fulfilProbePostal(store);
    if (!probe) return '';
    try {
      var line = api.localLine(store, probe, new Date());
      return line && line.ok && line.text ? String(line.text) : '';
    } catch (error) { return ''; }
  }
  function fulfilLocalLine(store, texts) {
    var fee = fulfilAmount(store.localFee);
    var min = fulfilAmount(store.localMin);
    var terms = fee && min && safeText(texts.local_fee_min) ? fmt(texts.local_fee_min, { fee: fee, min: min }) : safeText(texts.local_fee_checkout);
    var when = fulfilLocalWhen(store);
    return [terms, when].filter(Boolean).join(' · ');
  }
  // Returns the card block or null when the store offers neither service today
  function fulfilBlock(store, texts, tz) {
    if (!store) return null;
    var pickup = store.pickup === true && !fulfilPausedToday(store, 'pickup', tz);
    var local = store.local === true && !fulfilPausedToday(store, 'local', tz);
    if (!pickup && !local) return null;
    var pickupLabel = pickup ? safeText(texts.chip_pickup) : '';
    var localLabel = local ? safeText(texts.chip_local) : '';
    if (!pickupLabel && !localLabel) return null;
    var wrap = el('div', 'paex-shipping-pickup__fulfil');
    var pills = el('div', 'paex-shipping-pickup__fulfil-pills');
    if (pickupLabel) pills.appendChild(el('span', 'paex-shipping-pickup__fulfil-pill', pickupLabel));
    if (localLabel) pills.appendChild(el('span', 'paex-shipping-pickup__fulfil-pill', localLabel));
    wrap.appendChild(pills);
    if (pickup) { var ready = fulfilPickupLine(store, texts); if (ready) wrap.appendChild(el('span', 'paex-shipping-pickup__fulfil-line', ready)); }
    if (local) { var same = fulfilLocalLine(store, texts); if (same) wrap.appendChild(el('span', 'paex-shipping-pickup__fulfil-line', same)); }
    return wrap;
  }
  function openStoreDrawer(fallbackUrl) {
    var chip = document.querySelector('paex-store-chip');
    if (chip && typeof chip.openDrawer === 'function') {
      var drawer = document.getElementById(chip.dataset.drawerId);
      if (drawer && typeof drawer.show === 'function') { chip.openDrawer(); return true; }
    }
    if (fallbackUrl) { window.location.href = fallbackUrl; return true; }
    return false;
  }
  function readGeo() {
    if (typeof paexDeliveryReadGeo === 'function') return paexDeliveryReadGeo();
    return window.paexGeo || null;
  }
  function readDest() {
    return typeof paexDestRead === 'function' ? paexDestRead() : null;
  }

  /* ====================================================================
     Checker
     ==================================================================== */
  if (!customElements.get('paex-shipping-checker')) {
    customElements.define('paex-shipping-checker', class PaexShippingChecker extends HTMLElement {
      connectedCallback() {
        this.data = readJson(this, 'script[data-paex-shipping-data]');
        if (!this.data || typeof paexEddEstimate !== 'function') return;
        this.texts = this.data.texts || {};
        this.zones = parseZones(this.data.zones);
        this.rates = parseRates(this.data.rates);
        this.adminRates = null;
        var checker = this;
        adminRates(this.data.ratesUrl).then(function (data) { if (data) { checker.adminRates = data; checker.render(); } });
        this.abort = new AbortController();
        var signal = this.abort.signal;
        this.form = this.querySelector('[data-checker-form]');
        this.input = this.querySelector('[data-checker-input]');
        this.error = this.querySelector('[data-checker-error]');
        this.valid = this.querySelector('[data-checker-valid]');
        this.prompt = this.querySelector('[data-checker-prompt]');
        this.skeleton = this.querySelector('[data-checker-skeleton]');
        this.tile = this.querySelector('[data-checker-tile]');
        this.mapHost = this.querySelector('[data-checker-map]');
        this.liveKeys = {};
        this.cartRates = {};
        this.timelineOpen = false;
        this.typedThisPage = false;

        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this), { signal: signal });
        if (this.input) this.input.addEventListener('input', this.onInput.bind(this), { signal: signal });
        this.querySelectorAll('[data-checker-geo]').forEach(function (button) {
          button.addEventListener('click', this.useLocation.bind(this), { signal: signal });
        }, this);
        document.addEventListener(EV_DEST, this.render.bind(this), { signal: signal });
        document.addEventListener(EV_GEO, this.render.bind(this), { signal: signal });
        document.addEventListener('paex:offer-change', this.render.bind(this), { signal: signal }); // Free shipping offer - PE (2026-10-19)
        this.timer = setInterval(this.tick.bind(this), 60000);
        this.bindSticky();
        this.render();
      }
      disconnectedCallback() {
        if (this.abort) this.abort.abort();
        if (this.timer) clearInterval(this.timer);
        if (this.observer) this.observer.disconnect();
        if (this.mapObserver) this.mapObserver.disconnect();
        if (this.map && this.map.remove) { try { this.map.remove(); } catch (error) { /* already gone */ } }
      }

      /* ---- input ---- */
      onInput() {
        if (this.error) this.error.hidden = true;
        this.input.removeAttribute('aria-invalid');
        this.input.removeAttribute('data-valid');
        if (this.valid) this.valid.hidden = true;
      }
      onSubmit(event) {
        event.preventDefault();
        var raw = cleanPostal(this.input.value);
        if (!isFullPostal(raw)) {
          if (this.error) { var errText = this.error.querySelector('[data-checker-error-text]'); if (errText) errText.textContent = this.texts.error; this.error.hidden = false; }
          this.input.setAttribute('aria-invalid', 'true');
          this.input.focus();
          return;
        }
        this.input.removeAttribute('aria-invalid');
        this.input.setAttribute('data-valid', '');
        if (this.valid) this.valid.hidden = false;
        this.typedThisPage = true;
        var region = typeof paexProvinceFromPostal === 'function' ? paexProvinceFromPostal(raw) : null;
        var place = prettyPostal(raw);
        var self = this;
        var write = function (coords) {
          var dest = { region: region, place: place };
          if (coords) { dest.lat = coords.lat; dest.lng = coords.lng; }
          if (typeof paexDestWrite === 'function') paexDestWrite(dest); else self.render();
        };
        fsaTable(this.dataset.fsaUrl).then(function (table) { write(centroidFor(raw, table)); });
      }
      useLocation() {
        var self = this;
        if (!navigator.geolocation) return;
        var buttons = this.querySelectorAll('[data-checker-geo]');
        buttons.forEach(function (button) { button.disabled = true; });
        navigator.geolocation.getCurrentPosition(function (position) {
          buttons.forEach(function (button) { button.disabled = false; });
          var geo = readGeo() || {};
          var merged = { lat: position.coords.latitude, lng: position.coords.longitude, regionCode: geo.regionCode || '', regionName: geo.regionName || '', postal: geo.postal || '', city: geo.city || '' };
          window.paexGeo = merged;
          if (typeof paexDeliveryWriteGeoCache === 'function') paexDeliveryWriteGeoCache(merged);
          document.dispatchEvent(new CustomEvent(EV_GEO, { detail: merged }));
        }, function () {
          buttons.forEach(function (button) { button.disabled = false; });
          if (self.error) { self.error.hidden = false; var text = self.error.querySelector('[data-checker-error-text]'); if (text) text.textContent = self.texts.location_denied || self.texts.error; }
        }, { timeout: 8000, maximumAge: 300000 });
      }
      focusInput() {
        if (!this.input) return;
        this.input.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
        this.input.focus({ preventScroll: true });
      }
      tick() {
        if (!this.ctx || !this.lastResult) return;
        var estimate = paexEddEstimate(this.lastResult.transitKey, this.data.edd || {});
        if (!estimate || estimate.beforeCutoff !== this.lastResult.estimate.beforeCutoff) { this.render(); return; }
        if (!this.countdownNode || !this.countdownRow) return;
        var texts = this.texts;
        var countdown = paexEddFormatCountdown(estimate.msToCutoff);
        if (this.lastResult.lastDay) {
          var arriveBy = this.data.occasion && this.data.occasion.arriveBy ? paexEddFormatDate(dateFromKey(this.data.occasion.arriveBy)) : '';
          this.countdownNode.textContent = fmt(texts.last_day, { countdown: countdown, arrive: arriveBy });
        } else {
          var dispatchKey = dateKey(estimate.dispatchDate);
          var tomorrow = dateKey(paexEddAddDays(dateFromKey(this.lastResult.today), 1));
          var shipDay = dispatchKey === tomorrow ? texts.tomorrow : paexEddFormatDate(estimate.dispatchDate);
          this.countdownNode.textContent = fmt(texts.order_within, { countdown: countdown, day: shipDay });
        }
      }

      /* ---- destination resolution (shared chain, same as PDP + cart) ---- */
      resolveContext() {
        var dest = readDest();
        var geo = readGeo();
        var ctx = { state: 'a', soft: false, postal: '', region: '', place: '', city: '', coords: null };
        if (dest && dest.region) {
          ctx.region = dest.region;
          ctx.place = dest.place || '';
          if (dest.place && isPostalGrade(dest.place)) ctx.postal = cleanPostal(dest.place);
          if (isFinite(dest.lat) && isFinite(dest.lng)) ctx.coords = { lat: Number(dest.lat), lng: Number(dest.lng) };
          ctx.state = 'c';
          return ctx;
        }
        var customerZip = cleanPostal(this.dataset.customerZip);
        if (isFullPostal(customerZip)) {
          ctx.postal = customerZip;
          ctx.region = (typeof paexProvinceFromPostal === 'function' && paexProvinceFromPostal(customerZip)) || this.dataset.customerRegion || '';
          ctx.place = prettyPostal(customerZip);
          ctx.state = 'c';
          return ctx;
        }
        var geoCoords = geo && isFinite(geo.lat) && isFinite(geo.lng) ? { lat: Number(geo.lat), lng: Number(geo.lng) } : null;
        if (geo && (geo.postal || geo.regionCode || geoCoords)) {
          ctx.soft = true;
          ctx.state = 'b';
          ctx.postal = isPostalGrade(geo.postal) ? cleanPostal(geo.postal) : '';
          ctx.region = geo.regionCode || (ctx.postal && typeof paexProvinceFromPostal === 'function' ? paexProvinceFromPostal(ctx.postal) : '') || '';
          if (!ctx.region && geoCoords) {
            // browser position only (no geo-IP region): the nearest store's province is the best guess
            var near = nearestStores(geoCoords, this.data.stores, 1)[0];
            ctx.region = near && near.store.province ? String(near.store.province).toUpperCase() : '';
          }
          if (!ctx.region) return ctx.state = 'a', ctx;
          ctx.city = geo.city || '';
          ctx.place = geo.city || (ctx.postal ? prettyPostal(ctx.postal) : '') || this.provinceName(ctx.region);
          if (isFinite(geo.lat) && isFinite(geo.lng)) ctx.coords = { lat: Number(geo.lat), lng: Number(geo.lng) };
          return ctx;
        }
        return ctx;
      }
      provinceName(code) {
        var names = this.texts.provinces || {};
        return names[code] || code || '';
      }

      /* ---- estimate ---- */
      compute(ctx) {
        var edd = this.data.edd || {};
        var occasion = this.data.occasion || {};
        var zone = ctx.postal ? zoneFor(ctx.postal, this.zones) : null;
        var parsed = paexEddParseConfig(edd);
        var transitKey = ctx.region;
        if (zone === 'remote' && Object.prototype.hasOwnProperty.call(parsed.transit, ctx.region + '-R')) transitKey = ctx.region + '-R';
        var estimate = paexEddEstimate(transitKey, edd);
        if (!estimate) return null;
        var liveKey = ctx.postal && this.liveKeys[this.liveLookupKey(ctx)];
        var arrival = estimate.arrival;
        var arrivalKey = estimate.arrivalKey;
        if (liveKey && typeof paexEddLiveAccepts === 'function' && paexEddLiveAccepts(estimate, liveKey)) {
          arrivalKey = liveKey;
          arrival = dateFromKey(liveKey);
        }
        var today = todayKey(parsed.tz);
        var result = {
          zone: zone, estimate: estimate, arrival: arrival, arrivalKey: arrivalKey, today: today, parsed: parsed, transitKey: transitKey,
          occasionActive: Boolean(occasion.active && occasion.arriveBy),
          tooLate: false, lastDay: false, orderBy: null,
        };
        if (result.occasionActive) {
          result.tooLate = arrivalKey > occasion.arriveBy;
          if (!result.tooLate) {
            result.orderBy = paexEddOrderBy(transitKey, edd, occasion.arriveBy);
            result.lastDay = Boolean(result.orderBy && result.orderBy.key === today && estimate.beforeCutoff);
          }
        }
        // method + price (tiers from the admin, else the fallback table)
        var method = zone ? METHOD_BY_ZONE[zone] : null;
        var source = methodsFor(ctx.region, this.adminRates, this.rates);
        var methods = source ? source.methods : null;
        var rate = method && methods ? methods[method] : null;
        var cartRate = ctx.postal && this.cartRates[cleanPostal(ctx.postal)];
        result.method = method;
        result.exact = Boolean(zone && (rate || cartRate));
        result.rate = rate;
        result.cartRate = cartRate || null;
        result.range = methodsRange(methods);
        result.cartTotal = this.data.cart && this.data.cart.count > 0 ? Number(this.data.cart.total) / 100 : 0;
        return result;
      }
      liveLookupKey(ctx) { return (ctx.postal || ctx.city || ctx.region || '') + ':' + (this.data.edd && this.data.edd.processing || 0); }
      upgradeLive(ctx) {
        var proxy = this.data.liveProxy;
        var app = this.data.app;
        if (!proxy && !(app && app.key && app.variantId)) return;
        var key = this.liveLookupKey(ctx);
        if (this.liveKeys[key] !== undefined) return;
        this.liveKeys[key] = null; // in flight
        var self = this;
        var processing = this.data.edd && this.data.edd.processing;
        var fetcher = null;
        if (app && app.key && app.variantId && typeof paexEddAppFetch === 'function') {
          // app mode = the same AfterShip engine as the checkout EDD widget
          fetcher = paexEddAppFetch({ appKey: app.key, shop: app.shop, productId: app.productId, variantId: app.variantId, postal: ctx.postal ? prettyPostal(ctx.postal) : '', region: ctx.region, country: 'CA' });
        } else if (ctx.postal && typeof paexEddLiveFetch === 'function') {
          fetcher = paexEddLiveFetch(proxy, ctx.postal, processing);
        } else if (ctx.city && typeof paexEddLiveFetchCity === 'function') {
          fetcher = paexEddLiveFetchCity(proxy, ctx.city, ctx.region, 'CA', processing);
        }
        if (!fetcher) return;
        fetcher.then(function (liveKey) {
          self.liveKeys[key] = liveKey || false;
          if (liveKey) self.render();
        });
      }
      verifyCart(ctx) {
        if (!this.data.verifyCart || !this.data.cart || !(this.data.cart.count > 0)) return;
        if (!isFullPostal(ctx.postal) || this.cartRates[ctx.postal] !== undefined) return;
        this.cartRates[ctx.postal] = null;
        var self = this;
        var province = PROVINCE_EN[ctx.region] || ctx.region;
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        var address = { country: 'Canada', province: province, zip: prettyPostal(ctx.postal) };
        var query = 'shipping_address[zip]=' + encodeURIComponent(address.zip) + '&shipping_address[country]=Canada&shipping_address[province]=' + encodeURIComponent(province);
        var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        // Same two-step flow as the theme's shipping calculator (assets/cart.js):
        // POST prepare_shipping_rates.json, then poll async_shipping_rates.json.
        var poll = function (tries) {
          return fetch(cartUrl + '/async_shipping_rates.json?' + query, { headers: headers }).then(function (response) {
            if (!response.ok) return null;
            return response.text().then(function (text) {
              var data = null;
              try { data = text && text.trim() ? JSON.parse(text) : null; } catch (error) { data = null; }
              if (data && data.shipping_rates) return data;
              if (tries > 0) return new Promise(function (resolve) { setTimeout(function () { resolve(poll(tries - 1)); }, 2000); });
              return null;
            });
          });
        };
        var attempt = function (tries) {
          return fetch(cartUrl + '/prepare_shipping_rates.json', { method: 'POST', headers: headers, body: JSON.stringify({ shipping_address: address }) })
            .then(function (response) { return response.ok ? poll(tries) : null; });
        };
        attempt(3).then(function (data) {
          var list = data && data.shipping_rates;
          if (!list || !list.length) { self.cartRates[ctx.postal] = false; return; }
          var best = null;
          list.forEach(function (rate) {
            var price = parsePrice(rate.price);
            if (price == null) return;
            if (!best || price < best.price) best = { name: rate.name || rate.presentment_name || '', price: price };
          });
          self.cartRates[ctx.postal] = best || false;
          if (best) self.render();
        }).catch(function () { self.cartRates[ctx.postal] = false; });
      }

      /* ---- render ---- */
      render() {
        var ctx = this.resolveContext();
        this.ctx = ctx;
        var texts = this.texts;
        var show = function (node, on) { if (node) node.hidden = !on; };
        if (this.input && !this.typedThisPage && ctx.postal && !ctx.soft && document.activeElement !== this.input) {
          this.input.value = prettyPostal(ctx.postal);
        }
        if (ctx.state === 'a') {
          show(this.prompt, true); show(this.skeleton, false); show(this.tile, false);
          this.updateSticky(null);
          this.dispatchResolve(ctx, null);
          return;
        }
        var result = this.compute(ctx);
        if (!result) { show(this.prompt, true); show(this.tile, false); return; }
        show(this.prompt, false); show(this.skeleton, false); show(this.tile, true);
        this.lastResult = result;
        this.paintTile(ctx, result);
        this.updateSticky(ctx, result);
        this.dispatchResolve(ctx, result);
        if (ctx.postal || ctx.city || ctx.region) this.upgradeLive(ctx);
        this.verifyCart(ctx);
        this.renderMap(ctx, result);
      }
      dispatchResolve(ctx, result) {
        var next = {
          region: ctx.region || null, zone: result ? result.zone : null, postal: ctx.postal || null,
          mode: result && result.exact ? 'exact' : 'province', soft: ctx.soft,
        };
        if (lastResolve && JSON.stringify(lastResolve) === JSON.stringify(next)) return;
        lastResolve = next;
        document.dispatchEvent(new CustomEvent(EV_RESOLVE, { detail: lastResolve }));
      }
      // Free shipping offer - PE (2026-10-19, design S1): the offer line for the
      // resolved method — "Free over 99 $*" under Standard, "10 $ off over 99 $*"
      // under Regional, nothing for Remote, nothing while the
      // master switch is off (no engine on the page).
      offerRow(result) {
        var engine = window.paexShippingOffer;
        if (!engine || !engine.config || !engine.config.offers) return null;
        var method = result.cartRate ? paexOfferMethodOf(result.cartRate.name) : result.method;
        var zoneKey = method === 'standard' ? 'metro' : method;
        if (!zoneKey || zoneKey === 'express') return null;
        var text = paexOfferCellText(engine, zoneKey);
        return text ? { text: text, cls: 'paex-shipping-tile__row paex-shipping-tile__row--offer' } : null;
      }
      methodLine(ctx, result) {
        var texts = this.texts;
        var rows = this.methodRows(ctx, result);
        var offer = this.offerRow(result);
        if (offer) rows.splice(1, 0, offer);
        return rows;
      }
      methodRows(ctx, result) {
        var texts = this.texts;
        var rows = [];
        var methodName = result.method ? texts['method_' + result.method] : '';
        if (result.cartRate) {
          rows.push({ text: result.cartRate.name + ' · ' + money(result.cartRate.price), cls: 'paex-shipping-tile__row' });
          return rows;
        }
        if (result.exact) {
          var tiers = result.rate.tiers;
          if (result.cartTotal > 0 && tierFor(tiers, result.cartTotal)) {
            // a cart exists: the exact tier for this cart, no asterisk
            rows.push({ text: methodName + ' · ' + money(tierFor(tiers, result.cartTotal).price), cls: 'paex-shipping-tile__row' });
          } else {
            var band = overSplit(tiers);
            if (band && band.min === band.max) {
              rows.push({ text: methodName + ' · ' + money(band.min) + '*', cls: 'paex-shipping-tile__row' });
              rows.push({ text: texts.tier_note, cls: 'paex-shipping-tile__row paex-shipping-tile__row--note' });
            } else if (band) {
              rows.push({ text: methodName + ' · ' + fmt(texts.from_price, { min: money(band.min) }), cls: 'paex-shipping-tile__row' });
              rows.push({ text: texts.by_cart_value, cls: 'paex-shipping-tile__row paex-shipping-tile__row--note' });
            } else {
              rows.push({ text: methodName + ' · ' + texts.price_by_cart, cls: 'paex-shipping-tile__row' });
            }
          }
          return rows;
        }
        // PROVINCE mode
        var province = this.provinceName(ctx.region);
        if (result.zone === 'remote') {
          rows.push({ text: fmt(texts.province_remote, { province: province }), cls: 'paex-shipping-tile__row' });
        } else if (result.range) {
          var template = result.range.min === result.range.max ? texts.province_from : texts.province_range;
          rows.push({ text: fmt(template, { province: province, min: money(result.range.min), max: money(result.range.max) }), cls: 'paex-shipping-tile__row' });
        }
        rows.push({ text: texts.confirm_checkout, cls: 'paex-shipping-tile__row paex-shipping-tile__row--note' });
        return rows;
      }
      paintTile(ctx, result) {
        var texts = this.texts;
        var tile = this.tile;
        var estimate = result.estimate;
        var occasionName = (this.data.occasion && this.data.occasion.name) || '';
        var arriveBy = this.data.occasion && this.data.occasion.arriveBy ? paexEddFormatDate(dateFromKey(this.data.occasion.arriveBy)) : '';
        var tone = 'neutral';
        var eyebrow = texts.ship_home;
        var main = fmt(texts.arrives, { date: paexEddFormatDate(result.arrival) });
        var mainSmall = false;
        var badge = null;
        var rows = [];
        var showDot = true;
        var timeline = true;
        var pickup = null;

        if (result.tooLate) {
          tone = 'pickup';
          eyebrow = texts.in_store_today;
          main = fmt(texts.too_late, { occasion: occasionName });
          mainSmall = true;
          showDot = false;
          timeline = false;
          rows.push({ text: fmt(texts.ship_anyway, { date: paexEddFormatDate(result.arrival) }), cls: 'paex-shipping-tile__row paex-shipping-tile__row--small paex-shipping__muted' });
          pickup = this.nearestStore(ctx);
        } else {
          rows = this.methodLine(ctx, result);
          var cutoff = cutoffLabel(result.parsed.cutoff, texts);
          var dispatchKey = dateKey(estimate.dispatchDate);
          var tomorrow = dateKey(paexEddAddDays(dateFromKey(result.today), 1));
          var shipDay = dispatchKey === tomorrow ? texts.tomorrow : paexEddFormatDate(estimate.dispatchDate);
          if (estimate.beforeCutoff && !result.lastDay) {
            rows.push({ text: fmt(texts.order_within, { countdown: paexEddFormatCountdown(estimate.msToCutoff), day: shipDay }), cls: 'paex-shipping-tile__row paex-shipping-tile__row--strong', countdown: true });
          } else if (!estimate.beforeCutoff) {
            rows.push({ text: fmt(texts.ships_on, { date: paexEddFormatDate(estimate.dispatchDate) }), cls: 'paex-shipping-tile__row paex-shipping-tile__row--strong' });
          }
          if (result.zone === 'remote') rows.push({ text: texts.remote_note, cls: 'paex-shipping-tile__row paex-shipping-tile__row--small paex-shipping__muted' });
          if (result.occasionActive) {
            if (result.lastDay) {
              tone = 'amber';
              badge = texts.last_day_badge;
              rows.push({ text: fmt(texts.last_day, { countdown: paexEddFormatCountdown(estimate.msToCutoff), arrive: arriveBy }), cls: 'paex-shipping-tile__row paex-shipping-tile__row--amber', countdown: true });
            } else {
              tone = 'green';
              badge = fmt(texts.in_time, { occasion: occasionName });
              if (result.orderBy) rows.push({ text: cutoffOrderBy(texts, { occasion: occasionName, date: paexEddFormatDate(result.orderBy.orderBy), time: cutoff, arrive: arriveBy }), cls: 'paex-shipping-tile__row paex-shipping-tile__row--small' });
            }
          }
        }

        tile.setAttribute('data-tone', tone);
        var eyebrowNode = tile.querySelector('[data-tile-eyebrow]'); if (eyebrowNode) eyebrowNode.textContent = eyebrow;
        var badgeNode = tile.querySelector('[data-tile-badge]');
        if (badgeNode) { badgeNode.hidden = !badge; var badgeText = badgeNode.querySelector('[data-tile-badge-text]'); if (badgeText) badgeText.textContent = badge || ''; }
        var mainNode = tile.querySelector('[data-tile-main]');
        if (mainNode) {
          mainNode.classList.toggle('paex-shipping-tile__main--sm', mainSmall);
          var dot = mainNode.querySelector('[data-tile-dot]'); if (dot) dot.hidden = !showDot;
          var mainText = mainNode.querySelector('[data-tile-main-text]'); if (mainText) mainText.textContent = main;
        }
        var rowsNode = tile.querySelector('[data-tile-rows]');
        this.countdownNode = null;
        if (rowsNode) {
          clear(rowsNode);
          var self = this;
          rows.forEach(function (row) {
            var node = el('p', row.cls, row.text);
            if (row.countdown) { self.countdownNode = node; self.countdownRow = row; }
            rowsNode.appendChild(node);
          });
        }
        // a11y: announce only state changes (never the minute tick)
        var statusKey = tone + '|' + eyebrow + '|' + main;
        var statusNode = tile.querySelector('[data-tile-status]');
        if (statusNode && this.statusKey !== statusKey) { this.statusKey = statusKey; statusNode.textContent = eyebrow + '. ' + main; }

        // carriers (hidden in PROVINCE mode and in the pickup state)
        var carriersNode = tile.querySelector('[data-tile-carriers]');
        if (carriersNode) {
          var showCarriers = result.exact && !result.tooLate && result.zone;
          carriersNode.hidden = !showCarriers;
          if (showCarriers) this.paintCarriers(carriersNode, result.zone);
        }
        // timeline
        var tlNode = tile.querySelector('[data-tile-timeline]');
        if (tlNode) { tlNode.hidden = !timeline; if (timeline) this.paintTimeline(tlNode, ctx, result); }
        // pickup
        var pickupNode = tile.querySelector('[data-tile-pickup]');
        if (pickupNode) { pickupNode.hidden = !pickup && !result.tooLate; if (result.tooLate) this.paintPickup(pickupNode, pickup); }
        // foot
        var lead = tile.querySelector('[data-tile-foot-lead]'); if (lead) lead.textContent = ctx.soft ? texts.by_location : '';
        var place = tile.querySelector('[data-tile-place]'); if (place) place.textContent = ctx.place || this.provinceName(ctx.region);
        var deliverTo = tile.querySelector('[data-tile-deliver-to]'); if (deliverTo) deliverTo.textContent = fmt(texts.deliver_to, { place: '' }).replace(/\s+$/, '') + ' ';
        var change = tile.querySelector('[data-tile-change]');
        if (change && !change.dataset.bound) {
          change.dataset.bound = '1';
          change.addEventListener('click', this.focusInput.bind(this), { signal: this.abort.signal });
        }
      }
      paintCarriers(node, zone) {
        var texts = this.texts;
        var carriers = this.data.carriers || {};
        var list = node.querySelector('[data-tile-carrier-list]') || node;
        clear(list);
        var chip = function (key) {
          var carrier = carriers[key] || {};
          var span = el('span', 'paex-shipping-carrier');
          if (carrier.src) { var img = document.createElement('img'); img.src = carrier.src; img.alt = ''; img.width = 20; img.height = 20; img.loading = 'lazy'; span.appendChild(img); }
          span.appendChild(el('span', null, texts['carrier_' + key] || key));
          return span;
        };
        if (zone === 'remote') { list.appendChild(chip('canada_post')); return; }
        list.appendChild(chip('ups'));
        if (zone === 'regional') { list.appendChild(el('span', 'paex-shipping__muted', texts.or_sometimes)); list.appendChild(chip('canada_post')); }
      }
      paintTimeline(node, ctx, result) {
        var texts = this.texts;
        var estimate = result.estimate;
        var toggle = node.querySelector('[data-tl-toggle]');
        var list = node.querySelector('[data-tl-list]');
        if (toggle && !toggle.dataset.bound) {
          toggle.dataset.bound = '1';
          var self = this;
          toggle.addEventListener('click', function () {
            self.timelineOpen = !self.timelineOpen;
            toggle.setAttribute('aria-expanded', self.timelineOpen ? 'true' : 'false');
            if (list) list.hidden = !self.timelineOpen;
          }, { signal: this.abort.signal });
        }
        if (list) list.hidden = !this.timelineOpen;
        if (!list) return;
        clear(list);
        var orderDayKey = dateKey(estimate.orderDay);
        var ordered = (orderDayKey === result.today ? texts.today : paexEddFormatDate(estimate.orderDay)) +
          (estimate.beforeCutoff ? ' · ' + fmt(texts.before_cutoff, { time: cutoffLabel(result.parsed.cutoff, texts) }) : '');
        var processing = estimate.processingDays;
        var prepared = processing === 1 ? texts.business_day : fmt(texts.business_days, { n: processing });
        var items = [
          [texts.step_ordered, ordered],
          [texts.step_prepared, prepared],
          [texts.step_shipped, paexEddFormatDate(estimate.dispatchDate)],
          [texts.step_delivered, paexEddFormatDate(result.arrival) + ' · ' + fmt(texts.in_transit, { n: estimate.transitDays })],
        ];
        items.forEach(function (item) {
          var li = el('li', 'paex-shipping-tile__tl-item');
          li.appendChild(el('span', 'paex-shipping-tile__tl-rail')).setAttribute('aria-hidden', 'true');
          var text = el('span', 'paex-shipping-tile__tl-text');
          text.appendChild(el('b', null, item[0]));
          text.appendChild(el('span', null, item[1]));
          li.appendChild(text);
          list.appendChild(li);
        });
      }
      nearestStore(ctx) {
        var origin = ctx.coords;
        if (!origin) { var geo = readGeo(); if (geo && isFinite(geo.lat) && isFinite(geo.lng)) origin = { lat: Number(geo.lat), lng: Number(geo.lng) }; }
        if (!origin) return null;
        var list = nearestStores(origin, this.data.stores, 1);
        return list[0] || null;
      }
      paintPickup(node, nearest) {
        var texts = this.texts;
        var name = node.querySelector('[data-pickup-name]');
        var meta = node.querySelector('[data-pickup-meta]');
        var cta = node.querySelector('[data-pickup-cta]');
        if (name) name.textContent = nearest ? nearest.store.name : '';
        if (name) name.hidden = !nearest;
        if (meta) {
          clear(meta);
          meta.hidden = !nearest;
          if (nearest) {
            var hours = hoursText(nearest.store, texts, this.data.edd && this.data.edd.tz);
            if (hours) { meta.appendChild(el('b', null, hours.split(' · ')[0])); var rest = hours.split(' · ').slice(1).join(' · '); meta.appendChild(document.createTextNode((rest ? ' · ' + rest : '') + ' · ')); }
            meta.appendChild(document.createTextNode(kmLabel(texts, nearest.km)));
          }
        }
        if (cta && !cta.dataset.bound) {
          cta.dataset.bound = '1';
          var self = this;
          cta.addEventListener('click', function (event) {
            if (openStoreDrawer(null)) event.preventDefault();
          }, { signal: this.abort.signal });
        }
      }

      /* ---- sticky mini-checker (mobile) ---- */
      bindSticky() {
        this.sticky = this.closest('section, .shopify-section') ? this.closest('section, .shopify-section').querySelector('[data-paex-shipping-sticky]') : null;
        if (!this.sticky || !('IntersectionObserver' in window)) return;
        var self = this;
        this.stickyVisible = false;
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.cardOffscreen = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            self.sticky.hidden = !(self.cardOffscreen && self.stickyReady);
            document.documentElement.classList.toggle('paex-shipping-sticky-on', !self.sticky.hidden);
          });
        }, { threshold: 0 });
        this.observer.observe(this);
        var change = this.sticky.querySelector('[data-sticky-change]');
        if (change) change.addEventListener('click', this.focusInput.bind(this), { signal: this.abort.signal });
      }
      updateSticky(ctx, result) {
        if (!this.sticky) return;
        this.stickyReady = Boolean(ctx && result && !result.tooLate);
        if (!this.stickyReady) { this.sticky.hidden = true; document.documentElement.classList.remove('paex-shipping-sticky-on'); return; }
        var main = this.sticky.querySelector('[data-sticky-main]');
        var sub = this.sticky.querySelector('[data-sticky-sub]');
        if (main) main.textContent = fmt(this.texts.arrives, { date: paexEddFormatDate(result.arrival) });
        if (sub) {
          var parts = [fmt(this.texts.deliver_to, { place: ctx.place || this.provinceName(ctx.region) })];
          var line = this.methodLine(ctx, result)[0];
          if (line && result.exact) parts.push(line.text);
          sub.textContent = parts.join(' · ');
        }
        this.sticky.hidden = !this.cardOffscreen;
        document.documentElement.classList.toggle('paex-shipping-sticky-on', !this.sticky.hidden);
      }

      /* ---- route map (Mapbox GL, lazy; decorative) ---- */
      // The map is decorative and heavy (Mapbox GL): only for a HARD destination
      // (typed / stored / customer address, never a geo-IP guess), only once the
      // map host is in view, and redrawn only when destination or tone change.
      renderMap(ctx, result) {
        var host = this.mapHost;
        var map = this.data.map || {};
        if (!host || !map.token || !map.origin) return;
        if (!isFinite(Number(map.origin[0])) || !isFinite(Number(map.origin[1])) || Number(map.origin[0]) === 0) { host.hidden = true; return; }
        if (ctx.state !== 'c') { host.hidden = true; return; }
        host.hidden = false; // reserve the 200 px before any fetch (no layout shift later)
        var self = this;
        var destPromise = ctx.coords
          ? Promise.resolve(ctx.coords)
          : (ctx.postal ? fsaTable(this.dataset.fsaUrl).then(function (table) { return centroidFor(ctx.postal, table); }) : Promise.resolve(null));
        destPromise.then(function (dest) {
          if (!dest) { host.hidden = true; return; }
          var tone = result.tooLate ? 'late' : (result.lastDay ? 'amber' : (result.occasionActive ? 'green' : 'ink'));
          var key = dest.lat.toFixed(3) + ',' + dest.lng.toFixed(3) + '|' + tone;
          self.mapState = { dest: dest, tone: tone, ctx: ctx, result: result, key: key };
          self.paintMapLabel(ctx, result);
          if (self.mapDrawnKey === key) return;
          self.whenMapVisible(function () { self.loadMapbox(function () { self.drawMap(); }); });
        });
      }
      whenMapVisible(callback) {
        var host = this.mapHost;
        if (!('IntersectionObserver' in window)) { callback(); return; }
        if (this.mapObserver) this.mapObserver.disconnect();
        var self = this;
        this.mapObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) { self.mapObserver.disconnect(); callback(); }
          });
        }, { rootMargin: '120px' });
        this.mapObserver.observe(host);
      }
      paintMapLabel(ctx, result) {
        var label = this.mapHost.querySelector('[data-map-label]');
        if (!label) return;
        var main = label.querySelector('b');
        var sub = label.querySelector('span');
        if (main) main.textContent = fmt(this.texts.arrives, { date: paexEddFormatDate(result.arrival) });
        if (sub) {
          var line = result.tooLate ? fmt(this.texts.after_occasion, { occasion: (this.data.occasion && this.data.occasion.name) || '' }) : ((this.methodLine(ctx, result)[0] || {}).text || '');
          sub.textContent = line;
        }
      }
      loadMapbox(callback) {
        if (window.mapboxgl) { callback(); return; }
        if (this.mapLoading) { this.mapQueue.push(callback); return; }
        this.mapLoading = true;
        this.mapQueue = [callback];
        var self = this;
        var done = function () { self.mapLoading = false; var queue = self.mapQueue || []; self.mapQueue = []; queue.forEach(function (fn) { fn(); }); };
        var css = document.createElement('link'); css.rel = 'stylesheet'; css.href = MAPBOX_CSS; css.crossOrigin = 'anonymous'; document.head.appendChild(css);
        var script = document.createElement('script'); script.src = MAPBOX_JS; script.crossOrigin = 'anonymous';
        script.onload = done;
        script.onerror = function () { self.mapHost.hidden = true; self.mapLoading = false; };
        document.head.appendChild(script);
      }
      drawMap() {
        var state = this.mapState;
        var host = this.mapHost;
        var canvas = host.querySelector('[data-map-canvas]');
        var settings = this.data.map;
        if (!state || !canvas || !window.mapboxgl) return;
        if (!rgbVar(this, '--color-foreground') || !rgbVar(this, '--color-background')) return;
        if (this.mapDrawnKey === state.key) return;
        var firstDraw = !this.mapDrawnKey;
        this.mapDrawnKey = state.key;
        var origin = { lng: Number(settings.origin[1]), lat: Number(settings.origin[0]) };
        var dest = { lng: state.dest.lng, lat: state.dest.lat };
        var ink = rgbVar(this, '--color-foreground');
        var paper = rgbVar(this, '--color-background');
        var tones = {
          ink: ink,
          green: rgbVar(this, '--color-success') || ink,
          amber: rgbVar(this, '--color-warning') || ink,
          late: rgbVar(this, '--color-foreground', 0.35) || ink,
        };
        var popupColor = cssVar(host, '--paex-ship-popup') || tones.amber;
        var color = tones[state.tone] || ink;
        var self = this;
        var line = greatCircle([origin.lng, origin.lat], [dest.lng, dest.lat], 64);
        var nearest = null;
        var stores = this.data.stores || {};
        var storeFeatures = [];
        Object.keys(stores).forEach(function (id) {
          var store = stores[id];
          var lat = Number(store.lat), lng = Number(store.lng);
          if (!isFinite(lat) || !isFinite(lng)) return;
          if (typeof paexStoreExpired === 'function' && paexStoreExpired(store)) return;
          var km = distanceKm(dest, { lat: lat, lng: lng });
          if (km <= (Number(settings.nearKm) || 40) && (!nearest || km < nearest.km)) nearest = { id: id, lat: lat, lng: lng, km: km };
          storeFeatures.push({ type: 'Feature', properties: { popup: store.type === 'seasonal_popup' ? 1 : 0 }, geometry: { type: 'Point', coordinates: [lng, lat] } });
        });

        var build = function () {
          var map = self.map;
          var addOrSet = function (id, data) {
            if (map.getSource(id)) map.getSource(id).setData(data); else map.addSource(id, { type: 'geojson', data: data });
          };
          addOrSet('paex-stores', { type: 'FeatureCollection', features: storeFeatures });
          addOrSet('paex-route', { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: reducedMotion() ? line : line.slice(0, 2) } });
          addOrSet('paex-ends', { type: 'FeatureCollection', features: [
            { type: 'Feature', properties: { kind: 'origin' }, geometry: { type: 'Point', coordinates: [origin.lng, origin.lat] } },
            { type: 'Feature', properties: { kind: 'dest' }, geometry: { type: 'Point', coordinates: [dest.lng, dest.lat] } },
          ] });
          addOrSet('paex-near', { type: 'FeatureCollection', features: nearest ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [nearest.lng, nearest.lat] } }] : [] });
          if (!map.getLayer('paex-stores')) {
            map.addLayer({ id: 'paex-stores', type: 'circle', source: 'paex-stores', paint: {
              'circle-radius': 3.5, 'circle-stroke-width': 1.25, 'circle-stroke-color': paper,
              'circle-color': ['case', ['==', ['get', 'popup'], 1], popupColor, ink],
              'circle-opacity': ['case', ['==', ['get', 'popup'], 1], 1, 0.5],
            } });
            map.addLayer({ id: 'paex-near', type: 'circle', source: 'paex-near', paint: { 'circle-radius': 8, 'circle-color': tones.green, 'circle-opacity': 0, 'circle-stroke-width': 1.5, 'circle-stroke-color': tones.green } });
            map.addLayer({ id: 'paex-route', type: 'line', source: 'paex-route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-width': 2.5, 'line-color': color } });
            map.addLayer({ id: 'paex-ends', type: 'circle', source: 'paex-ends', paint: {
              'circle-radius': ['case', ['==', ['get', 'kind'], 'origin'], 4.5, 5.5],
              'circle-color': ['case', ['==', ['get', 'kind'], 'origin'], paper, color],
              'circle-stroke-width': 2, 'circle-stroke-color': ['case', ['==', ['get', 'kind'], 'origin'], ink, paper],
            } });
          } else {
            map.setPaintProperty('paex-route', 'line-color', color);
            map.setPaintProperty('paex-ends', 'circle-color', ['case', ['==', ['get', 'kind'], 'origin'], paper, color]);
          }
          map.setPaintProperty('paex-route', 'line-dasharray', state.tone === 'late' ? [1, 2] : [1, 0]);
          var bounds = new window.mapboxgl.LngLatBounds([origin.lng, origin.lat], [origin.lng, origin.lat]);
          bounds.extend([dest.lng, dest.lat]);
          if (nearest) bounds.extend([nearest.lng, nearest.lat]);
          map.fitBounds(bounds, { padding: { top: 56, bottom: 40, left: 36, right: 36 }, maxZoom: 10, duration: 0 });
          if (!reducedMotion() && firstDraw) self.animateRoute(line); else map.getSource('paex-route').setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } });
        };

        if (this.map) { build(); return; }
        try {
          window.mapboxgl.accessToken = settings.token;
          this.map = new window.mapboxgl.Map({
            container: canvas,
            style: settings.style || 'mapbox://styles/mapbox/light-v11',
            center: [origin.lng, origin.lat],
            zoom: 7,
            interactive: Boolean(settings.interactive),
            cooperativeGestures: Boolean(settings.interactive),
            attributionControl: true,
            dragRotate: false,
            pitchWithRotate: false,
          });
          if (settings.interactive) this.map.touchZoomRotate.disableRotation();
          this.map.on('load', build);
        } catch (error) {
          host.hidden = true;
        }
      }
      animateRoute(line) {
        var map = this.map;
        var source = map.getSource('paex-route');
        if (!source) return;
        var start = null;
        var total = line.length;
        var step = function (timestamp) {
          if (!start) start = timestamp;
          var t = Math.min(1, (timestamp - start) / 1000);
          var eased = 1 - Math.pow(1 - t, 3); // close to cubic-bezier(0,0,.3,1)
          var count = Math.max(2, Math.round(eased * total));
          source.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line.slice(0, count) } });
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    });
  }

  // Great-circle interpolation (lng/lat degrees) — a straight-looking local
  // line for Québec, a natural arc across the country.
  function greatCircle(a, b, steps) {
    var rad = Math.PI / 180;
    var lng1 = a[0] * rad, lat1 = a[1] * rad, lng2 = b[0] * rad, lat2 = b[1] * rad;
    var d = 2 * Math.asin(Math.sqrt(Math.pow(Math.sin((lat1 - lat2) / 2), 2) + Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin((lng1 - lng2) / 2), 2)));
    var points = [];
    if (!d || !isFinite(d)) return [a, b];
    for (var i = 0; i <= steps; i += 1) {
      var f = i / steps;
      var A = Math.sin((1 - f) * d) / Math.sin(d);
      var B = Math.sin(f * d) / Math.sin(d);
      var x = A * Math.cos(lat1) * Math.cos(lng1) + B * Math.cos(lat2) * Math.cos(lng2);
      var y = A * Math.cos(lat1) * Math.sin(lng1) + B * Math.cos(lat2) * Math.sin(lng2);
      var z = A * Math.sin(lat1) + B * Math.sin(lat2);
      points.push([Math.atan2(y, x) / rad, Math.atan2(z, Math.sqrt(x * x + y * y)) / rad]);
    }
    return points;
  }

  /* ====================================================================
     Rates table
     ==================================================================== */
  if (!customElements.get('paex-shipping-rates')) {
    customElements.define('paex-shipping-rates', class PaexShippingRates extends HTMLElement {
      connectedCallback() {
        this.data = readJson(this, 'script[data-paex-shipping-rates]') || {};
        this.texts = this.data.texts || {};
        this.rates = parseRates(this.data.rates);
        this.adminRates = null;
        var grid = this;
        adminRates(this.data.ratesUrl).then(function (data) { if (data) { grid.adminRates = data; grid.paint(); } });
        this.abort = new AbortController();
        var signal = this.abort.signal;
        this.select = this.querySelector('[data-province]');
        this.confirm = this.querySelector('[data-rates-confirm]');
        this.zone = null;
        this.mode = 'province';
        var self = this;
        if (this.select) this.select.addEventListener('change', function () { self.userProvince = self.select.value; self.paint(); }, { signal: signal });
        document.addEventListener(EV_RESOLVE, function (event) { self.onResolve(event.detail); }, { signal: signal });
        document.addEventListener('paex:offer-change', function () { self.paint(); }, { signal: signal }); // Free shipping offer - PE
        this.querySelectorAll('[data-why]').forEach(function (button) {
          button.addEventListener('click', function () { self.toggleTip(button); }, { signal: signal });
        });
        document.addEventListener('keydown', function (event) { if (event.key === 'Escape') self.closeTips(); }, { signal: signal });
        if (lastResolve) this.onResolve(lastResolve); else {
          var dest = readDest(); var geo = readGeo();
          var region = (dest && dest.region) || (geo && geo.regionCode) || null;
          if (region && this.select && this.hasOption(region)) this.select.value = region;
          this.paint();
        }
      }
      disconnectedCallback() { if (this.abort) this.abort.abort(); }
      hasOption(value) { return Boolean(this.select && Array.prototype.some.call(this.select.options, function (o) { return o.value === value; })); }
      onResolve(detail) {
        if (!detail) return;
        this.zone = detail.zone;
        this.mode = detail.mode;
        if (detail.region && this.select && !this.userProvince && this.hasOption(detail.region)) this.select.value = detail.region;
        var note = this.querySelector('[data-rates-note]');
        if (note) note.hidden = Boolean(this.userProvince) || !detail.region;
        this.paint();
      }
      toggleTip(button) {
        var tip = button.parentElement.parentElement.querySelector('[data-tip]');
        var open = button.getAttribute('aria-expanded') === 'true';
        this.closeTips();
        if (!open && tip) { tip.hidden = false; button.setAttribute('aria-expanded', 'true'); }
      }
      closeTips() {
        this.querySelectorAll('[data-tip]').forEach(function (tip) { tip.hidden = true; });
        this.querySelectorAll('[data-why]').forEach(function (button) { button.setAttribute('aria-expanded', 'false'); });
      }
      paint() {
        var province = this.select ? this.select.value : (this.data.defaultProvince || 'QC');
        var source = methodsFor(province, this.adminRates, this.rates);
        var methods = source ? source.methods : null;
        var exact = this.mode === 'exact' && !this.userProvince;
        var zoneMethod = this.zone ? METHOD_BY_ZONE[this.zone] : null;
        var texts = this.texts;
        this.querySelectorAll('tr[data-method]').forEach(function (row) {
          var method = row.dataset.method;
          var entry = methods ? methods[method] : null;
          var over = row.querySelector('[data-price-over]');
          var under = row.querySelector('[data-price-under]');
          var why = row.querySelector('[data-why]');
          var unavailable = !entry;
          if (unavailable && method === 'remote' && methods) {
            // no admin/table price for remote: it is tiered by cart value at checkout
            if (over) over.textContent = texts.by_cart_value;
            if (under) under.textContent = texts.by_cart_value;
          } else {
            var band = entry ? overSplit(entry.tiers) : null;
            var low = entry ? underSplit(entry.tiers) : null;
            if (over) over.textContent = band ? moneyRange(band) : '—';
            if (under) under.textContent = low != null ? money(low) : '—';
          }
          if (why) why.hidden = !(method === 'express' && unavailable);
          if (exact && zoneMethod === method) row.setAttribute('data-yours', ''); else row.removeAttribute('data-yours');
          // Free shipping offer - PE (design S1): offer cell under Standard / Regional prices
          var offerCell = row.querySelector('[data-fso-offer]');
          if (offerCell) {
            var engine = window.paexShippingOffer;
            var offerText = engine && (method === 'standard' || method === 'regional') ? paexOfferCellText(engine, method === 'standard' ? 'metro' : 'regional') : '';
            offerCell.textContent = offerText;
            offerCell.hidden = !offerText;
          }
        });
        this.closeTips();
        if (this.confirm) this.confirm.hidden = exact && Boolean(methods);
      }
    });
  }

  /* ====================================================================
     Store pickup list
     ==================================================================== */
  if (!customElements.get('paex-shipping-pickup')) {
    customElements.define('paex-shipping-pickup', class PaexShippingPickup extends HTMLElement {
      connectedCallback() {
        this.data = readJson(this, 'script[data-paex-shipping-pickup]') || {};
        this.texts = this.data.texts || {};
        this.abort = new AbortController();
        var signal = this.abort.signal;
        this.list = this.querySelector('[data-pickup-list]');
        this.note = this.querySelector('[data-pickup-note]');
        var self = this;
        document.addEventListener(EV_DEST, function () { self.render(); }, { signal: signal });
        document.addEventListener(EV_GEO, function () { self.render(); }, { signal: signal });
        // #35: the chosen store leads the list — re-render when the chip changes it
        document.addEventListener(typeof PAEX_STORE_EVENT === 'string' ? PAEX_STORE_EVENT : 'paex:store-change', function () { self.render(); }, { signal: signal });
        // #40: the block takes the SAME page container as "Shipping rates by province" — the
        // rates section's inner max-width, measured on this page (its own setting is the fallback)
        var rates = document.querySelector('.paex-shipping-rates .paex-shipping__inner');
        if (rates) {
          try { var width = getComputedStyle(rates).maxWidth; if (width && width !== 'none') this.style.maxWidth = width; } catch (error) { /* own setting */ }
        }
        this.querySelectorAll('[data-paex-open-drawer]').forEach(function (button) {
          button.addEventListener('click', function (event) { if (openStoreDrawer(null)) event.preventDefault(); }, { signal: signal });
        });
        this.querySelectorAll('[data-pickup-geo]').forEach(function (button) {
          button.addEventListener('click', function () { self.useLocation(button); }, { signal: signal });
        });
        this.render();
      }
      disconnectedCallback() { if (this.abort) this.abort.abort(); }
      useLocation(button) {
        if (!navigator.geolocation) return;
        var self = this;
        button.disabled = true;
        navigator.geolocation.getCurrentPosition(function (position) {
          button.disabled = false;
          var geo = readGeo() || {};
          var merged = { lat: position.coords.latitude, lng: position.coords.longitude, regionCode: geo.regionCode || '', regionName: geo.regionName || '', postal: geo.postal || '', city: geo.city || '' };
          window.paexGeo = merged;
          if (typeof paexDeliveryWriteGeoCache === 'function') paexDeliveryWriteGeoCache(merged);
          document.dispatchEvent(new CustomEvent(EV_GEO, { detail: merged }));
        }, function () {
          button.disabled = false;
          if (self.note) { self.note.hidden = false; self.note.textContent = self.texts.location_denied || ''; }
        }, { timeout: 8000, maximumAge: 300000 });
      }
      origin() {
        var dest = readDest();
        var geo = readGeo();
        if (dest && isFinite(dest.lat) && isFinite(dest.lng)) return Promise.resolve({ lat: Number(dest.lat), lng: Number(dest.lng) });
        if (dest && dest.place && isPostalGrade(dest.place)) {
          return fsaTable(this.dataset.fsaUrl).then(function (table) {
            var hit = centroidFor(dest.place, table);
            if (hit) return hit;
            return geo && isFinite(geo.lat) && isFinite(geo.lng) ? { lat: Number(geo.lat), lng: Number(geo.lng) } : null;
          });
        }
        if (geo && isFinite(geo.lat) && isFinite(geo.lng)) return Promise.resolve({ lat: Number(geo.lat), lng: Number(geo.lng) });
        return Promise.resolve(null);
      }
      // #35: the list LEADS with the chosen / resolved store whenever it is in the program today
      // (pickup or same-day enabled, not paused) — its pills, ready sentence and same-day line on
      // the card and the green outline (data-chosen) — then the nearest stores by origin, deduped.
      // A nearest-only card gets no outline. Without an origin the chosen store still renders alone.
      render() {
        var self = this;
        if (!this.list) return;
        var stores = this.data.stores || {};
        var chosenId = fulfilChosenStoreId(stores);
        var chosen = chosenId && fulfilParticipates(stores[chosenId], this.data.tz) ? stores[chosenId] : null;
        var limit = Number(this.data.limit) || 3;
        this.origin().then(function (origin) {
          var items = origin ? nearestStores(origin, stores, limit) : [];
          if (chosen) {
            var km = origin && isFinite(Number(chosen.lat)) && isFinite(Number(chosen.lng)) ? distanceKm(origin, { lat: Number(chosen.lat), lng: Number(chosen.lng) }) : NaN;
            items = [{ id: chosenId, store: chosen, km: km, chosen: true }]
              .concat(items.filter(function (item) { return String(item.id) !== String(chosenId); }))
              .slice(0, limit);
          }
          if (!items.length) { self.list.hidden = true; return; }
          clear(self.list);
          items.forEach(function (item) {
            var li = document.createElement('li');
            var page = typeof paexSafeUrl === 'function' ? paexSafeUrl(item.store.page) : '';
            var card = document.createElement(page ? 'a' : 'div');
            card.className = 'paex-shipping-pickup__card paex-shipping__surface color-' + (self.data.cardScheme || '');
            if (page) card.href = page;
            if (item.chosen) card.setAttribute('data-chosen', '');
            var main = el('div', 'paex-shipping-pickup__main');
            var nameRow = el('div', 'paex-shipping-pickup__name-row');
            if (item.chosen && safeText(self.texts.your_store)) nameRow.appendChild(el('span', 'visually-hidden', self.texts.your_store + ': '));
            nameRow.appendChild(el('span', 'paex-shipping-pickup__name', item.store.name || ''));
            if (item.store.type === 'seasonal_popup') nameRow.appendChild(el('span', 'paex-shipping-pickup__badge', self.texts.legend_popup));
            main.appendChild(nameRow);
            var meta = el('span', 'paex-shipping-pickup__meta');
            var hours = hoursText(item.store, self.texts, self.data.tz);
            var hasKm = isFinite(item.km);
            if (hours) { var bits = hours.split(' · '); meta.appendChild(el('b', null, bits[0])); meta.appendChild(document.createTextNode((bits.length > 1 ? ' · ' + bits.slice(1).join(' · ') : '') + (hasKm ? ' · ' : ''))); }
            if (hasKm) meta.appendChild(document.createTextNode(kmLabel(self.texts, item.km)));
            main.appendChild(meta);
            // BOPIS: pickup / same-day pills + ready and same-day lines for a participating store
            var fulfil = fulfilBlock(item.store, self.texts, self.data.tz);
            if (fulfil) main.appendChild(fulfil);
            card.appendChild(main);
            if (page) card.appendChild(el('span', 'paex-shipping-pickup__view', self.texts.view));
            li.appendChild(card);
            self.list.appendChild(li);
          });
          self.list.hidden = false;
          if (self.note) self.note.hidden = true;
        });
      }
    });
  }

  /* ====================================================================
     Help chat opener, reviews arrows, performance line
     ==================================================================== */
  if (!window.paexShippingChatBound) window.paexShippingChatBound = true, document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-paex-chat]');
    if (!trigger) return;
    var chat = window.GorgiasChat;
    if (chat && typeof chat.open === 'function') { event.preventDefault(); try { chat.open(); } catch (error) { /* fall through to the link */ } }
  });

  if (!customElements.get('paex-shipping-reviews')) {
    customElements.define('paex-shipping-reviews', class PaexShippingReviews extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-reviews-track]');
        if (!this.track) return;
        this.abort = new AbortController();
        var self = this;
        this.querySelectorAll('[data-reviews-prev], [data-reviews-next]').forEach(function (button) {
          button.addEventListener('click', function () {
            var card = self.track.firstElementChild;
            var gap = parseFloat(getComputedStyle(self.track).columnGap) || 0;
            var width = card ? card.getBoundingClientRect().width + gap : self.track.clientWidth;
            self.track.scrollBy({ left: button.hasAttribute('data-reviews-prev') ? -width : width, behavior: reducedMotion() ? 'auto' : 'smooth' });
          }, { signal: self.abort.signal });
        });
      }
      disconnectedCallback() { if (this.abort) this.abort.abort(); }
    });
  }

  if (!customElements.get('paex-shipping-perf')) {
    customElements.define('paex-shipping-perf', class PaexShippingPerf extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        var self = this;
        document.addEventListener(EV_RESOLVE, function (event) { self.show(event.detail && event.detail.region); }, { signal: this.abort.signal });
        var dest = readDest(); var geo = readGeo();
        this.show((lastResolve && lastResolve.region) || (dest && dest.region) || (geo && geo.regionCode) || null);
      }
      disconnectedCallback() { if (this.abort) this.abort.abort(); }
      show(region) {
        var entries = this.querySelectorAll('[data-perf-province]');
        if (!entries.length) return;
        var match = null;
        entries.forEach(function (entry) { if (region && entry.dataset.perfProvince === region) match = entry; });
        entries.forEach(function (entry, index) { entry.hidden = match ? entry !== match : index !== 0; });
      }
    });
  }
})();
